import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Mail,
  Lock,
  User,
  Loader2,
  CheckCircle2,
  Gift,
  Shield,
  Calendar,
  Users,
  FileText,
  Wrench,
  Eye,
  EyeOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialRegistration() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'trustee',
    company_name: '',
  });

  // Pre-fill email if arriving from the login page ("no account" redirect).
  useEffect(() => {
    const emailQ = searchParams.get('email');
    if (emailQ) setForm((f) => (f.email ? f : { ...f, email: emailQ }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    } else if (form.full_name.trim().length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }
    
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!form.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (form.password !== form.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    if (form.role === 'managing_agent' && !form.company_name.trim()) {
      newErrors.company_name = 'Company name is required for managing agents';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/trial/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.toLowerCase().trim(),
          password: form.password,
          confirm_password: form.confirm_password,
          role: form.role,
          company_name: form.company_name.trim() || null,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store session token
        localStorage.setItem('session_token', data.session_token);
        
        // Show success
        setStep(2);
        
        toast.success('Welcome to BodyCorp365!');
        
        // Redirect after a moment
        setTimeout(() => {
          // Set the user in auth context
          setUser({
            user_id: data.user_id,
            email: data.email,
            name: data.name,
            role: data.role,
            secondary_roles: data.secondary_roles || [],
            role_selected: data.role_selected !== false,
            is_trial: true,
            trial_end_date: data.trial_end_date,
          });
          
          // Redirect to building onboarding for new trial users
          navigate('/building-onboarding');
        }, 2000);
      } else {
        toast.error(data.detail || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Users, text: 'Manage communication with residents' },
    { icon: Wrench, text: 'Track maintenance requests' },
    { icon: FileText, text: 'Store documents securely' },
    { icon: Shield, text: 'Run digital voting' },
  ];

  // Success screen - Post-signup welcome message
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center animate-in fade-in zoom-in duration-500">
          <CardContent className="pt-12 pb-8">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome to BodyCorp365!</h2>
            <p className="text-slate-600 mb-2">
              Your 30-day free trial has started.
            </p>
            <p className="text-slate-700 font-medium mb-6">
              You now have full access to all features.<br />
              Create your building and start exploring.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-emerald-700 font-medium">
                <CheckCircle2 className="w-5 h-5" />
                <span>Full access unlocked</span>
              </div>
              <p className="text-sm text-emerald-600 mt-1">30 days of unlimited features</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting to your dashboard...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900" data-testid="trial-registration-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-white flex-shrink-0">
            <Building2 className="w-8 h-8" />
            <span className="text-xl font-bold hidden sm:inline">BodyCorp365</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="text-white border-white/30 hover:bg-white/10 text-sm sm:text-base whitespace-nowrap">
              <span className="hidden sm:inline">Already have an account? </span>Login
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Benefits */}
          <div className="hidden lg:block text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <Gift className="w-5 h-5 text-amber-400" />
              <span className="font-medium">No Credit Card Required</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Start Your Free 30-Day Trial
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Set up your building and start managing everything in one place.
            </p>
            
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-blue-200" />
                  </div>
                  <span className="text-lg">{feature.text}</span>
                </div>
              ))}
            </div>

            <div className="p-4 bg-white/10 rounded-xl border border-white/20">
              <p className="text-sm text-blue-100 italic">
                "Used by trustees and managing agents to simplify building management."
              </p>
            </div>
          </div>

          {/* Right side - Registration Form */}
          <Card className="w-full shadow-2xl" data-testid="registration-form-card">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 lg:hidden">
                <Gift className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Start Your Free 30-Day Trial</CardTitle>
              <CardDescription className="text-base">
                No credit card required. Set up your building and start managing everything in one place.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="full_name"
                      type="text"
                      placeholder="John Smith"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className={`pl-10 ${errors.full_name ? 'border-red-500' : ''}`}
                      data-testid="input-full-name"
                    />
                  </div>
                  {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="input-email"
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div>
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={form.confirm_password}
                      onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                      className={`pl-10 pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                      data-testid="input-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirm_password && <p className="text-xs text-red-500 mt-1">{errors.confirm_password}</p>}
                </div>

                {/* Role Selection */}
                <div>
                  <Label htmlFor="role">I am a...</Label>
                  <Select 
                    value={form.role} 
                    onValueChange={(value) => setForm({ ...form, role: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resident">Resident</SelectItem>
                      <SelectItem value="trustee">Trustee + Resident</SelectItem>
                      <SelectItem value="managing_agent">Managing Agent / Property Manager</SelectItem>
                      <SelectItem value="owner">Property Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Company Name (for Managing Agents) */}
                {form.role === 'managing_agent' && (
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <div className="relative mt-1">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="company_name"
                        type="text"
                        placeholder="Your company name"
                        value={form.company_name}
                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                        className={`pl-10 ${errors.company_name ? 'border-red-500' : ''}`}
                        data-testid="input-company-name"
                      />
                    </div>
                    {errors.company_name && <p className="text-xs text-red-500 mt-1">{errors.company_name}</p>}
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-lg font-semibold"
                  disabled={loading}
                  data-testid="submit-registration"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <ArrowRight className="w-5 h-5 mr-2" />
                      Start Free Trial
                    </>
                  )}
                </Button>

                {/* Trust line */}
                <p className="text-sm text-center text-slate-600 font-medium">
                  Used by trustees and managing agents to simplify building management.
                </p>

                {/* Terms */}
                <p className="text-xs text-center text-slate-500">
                  By registering, you agree to our{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                </p>
              </form>

              {/* Already have account */}
              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-slate-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-600 font-medium hover:underline">
                    Login here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
